import { Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import { ActionState } from 'services/EpdClient';
import {
  useCompilerCompositeStatus,
  useEPDDefinitionInformation,
  useEpdLatestVersionId,
  useGetEpdPCRInformation,
  useGetEpdThirdPartyVerification,
} from 'services/api/queries';

import CompilerStepsValidator, { CompilerStepState } from './CompilerStepsValidator';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Step5 from './Step5';
import Step6 from './Step6';
import { IsReadOnlyModeContext } from './hooks/useIsReadOnlyMode';
import { CONSTRUCTION_PCR_V2_ID } from 'constants/constants';
import { PcrV2Context } from './hooks/usePcrV2';
import { IsCreatedByApiFullyIntegratedPreverifiedToolContext } from './hooks/useIsCreatedByApiFullyIntegratedPreverifiedTool';
import useEpdId from './hooks/useEpdId';

const compilerStepsValidator = new CompilerStepsValidator()
  .registerStep(1, (step) => {
    step.withFields(
      'generalInformation',
      // EPD Definition
      'title',
      'statementOfConformityWithStandards',
      'versionDescription',
      'requirePublicationOnOkobaudat',
      'coverPageProductImage',
      'useAdvice',
      'subtype',
      'epdClassification',
      'representativeJustification',
      'determinationOfAverageDescription',
      'listOfContributingManufacturers',
      'sitesSelectionDescription',
      'documentCoverageStatement',
      'productToDeclare',
      'functionalUnitDescription',
      'productFlow',
      'productFlowName',
      'productFlowUnit',
      'productFlowValue',
      'conversionFactor',
      'conversionFactorUnit',
      'conversionFactorToMass',
      'massOfDeclaredUnit',
      'customLogotype',
      // PCR, LCA, EPD verification
      'pcr',
      'cpcrProductCategory',
      'lcaPractitioners',
      'verificationType',
      'thirdPartyVerification',
      'processCertificateReports',
      'preverifiedTool',
      'preverifiedToolVersion',
      'epdVerifier',
      'epdVerifierUnavailable',
      // Organization information
      'companyLogoImages',
      'organizationInformation',
      'organizationInformationAddress',
      // Additional information
      'epdAdditionalInformation',
      'abbreviations',
      'references'
    );
  })
  .registerStep(2, (step) => {
    step.withFields(
      'products',
      /^products\[\d+\]$/,
      // Content declaration
      'serviceIncludesProductInSystemBoundary',
      'declarationIrrelevanceExplanation',
      'averageProductContentDeclarationDescription',
      'averageProductComponents',
      'packagingMaterialApplicable',
      'averagePackagingMaterials',
      'averagePanelContents',
      'averageDangerMaterials',
      'comparisonWorstAndBestCase',
      // Technical performance
      'technicalPerformance',
      'estimatedServiceLife'
    );
  })
  .registerStep(3, (step) => {
    step.withFields(
      // Specification
      'infrastructureAndCapitalGoods',
      'dataQualityAssessment',
      'lcaSpecification',
      'epdDataSource',
      /^epdDataSource\[\d+\]$/,
      'datasourceSectionErrorDatabaseVersion',
      'otherDatabaseUsedAsDatasourceDescription',
      /^electricityItems\[\d+\]$/,
      'lcaSoftwareVersionId',
      'lcaSoftwareId',
      'otherSoftwareVersion',
      'otherSoftwareName',
      'referencePackageVersion15804',
      'technologyDescription',
      'scrapInputs',
      'epdShareOfTotalScrapInput',
      'scrapInputTotalPercent',
      'biogasData',
      'characterisationMethods',
      // System boundary
      'systemBoundaryDescription',
      'hasExcludedModules',
      'excludedModulesJustification',
      'declaredModulesErrors',
      'processFlowDiagramsImages',
      // Scenarios
      'scenarios',
      // Results
      'lcaResults',
      'cO2Carbonation',
      'declarationOfCO2Assumptions',
      'transportationPerformance',
      'justificationForInclusion'
    );
  })
  .registerStep(4, (step) => {
    step.withFields('verificationReports');
  })
  .registerStep(5, (step) => {
    step.withFields('firstPublishedVersionDate', 'versionDate', 'validityDate', 'serviceAgreementStatus');
  });

const useCompilerLogic = () => {
  const history = useHistory();
  const id = useEpdId();

  const match = useRouteMatch<{ id: string; stepId: string }>({ path: EpdRoutes.epd + '/:stepId*' });
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const stepId = match ? match.params.stepId : undefined;
  const tab = query.get('tab');
  const redirect =
    !match || !match.params.stepId ? (
      <Redirect to={EpdLinks.epdCompilerSpecific(id, { step: 'step1', tab: 'epd-definition' })} />
    ) : null;

  const epdVersionId = useEpdLatestVersionId(id).data;
  const compilerCompositeStatus = useCompilerCompositeStatus(id).data;
  const epdDefinition = useEPDDefinitionInformation(epdVersionId!).data;
  const pcrInfromation = useGetEpdPCRInformation(epdVersionId!).data;
  const thirdPartyVerification = useGetEpdThirdPartyVerification(epdVersionId!).data;
  const isPcrV2 = !pcrInfromation ? undefined : pcrInfromation.pcr?.id === CONSTRUCTION_PCR_V2_ID;
  const isCreatedByApiFullyIntegratedPreverifiedTool = thirdPartyVerification?.isCreatedByApiFullyIntegratedPreverifiedTool;
  type StepDefinition = {
    id: string;
    label: string;
    name: string;
    description: string;
    path: string;
    render: () => JSX.Element;
    stepState: (errors: any) => CompilerStepState | undefined;
  };

  const isMainContentReadOnly = compilerCompositeStatus?.fieldsState.mainContent !== ActionState.Enabled;
  const steps: StepDefinition[] = [
    {
      id: 'step1',
      label: 'Step 1',
      name: 'General information',
      description: 'Fill-in the EPD initial data',
      path: EpdLinks.epdCompilerSpecific(id, { step: 'step1', tab: 'epd-definition' }),
      render: () => {
        return (
          <PcrV2Context.Provider value={isPcrV2}>
            <IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider
              value={isCreatedByApiFullyIntegratedPreverifiedTool}
            >
              <IsReadOnlyModeContext.Provider value={isMainContentReadOnly}>
                <Step1 epdId={id} epdVersionId={epdVersionId!} tab={tab} validationState={compilerCompositeStatus} />
              </IsReadOnlyModeContext.Provider>
            </IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider>
          </PcrV2Context.Provider>
        );
      },
      stepState: (errors) => compilerStepsValidator.getStepState(1, errors),
    },
    {
      id: 'step2',
      label: 'Step 2',
      name: 'Product information',
      description: 'Describe your EPD subject',
      path: EpdLinks.epdCompilerSpecific(id, { step: 'step2', tab: 'product-information' }),
      render: () => {
        return (
          <PcrV2Context.Provider value={isPcrV2}>
            <IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider
              value={isCreatedByApiFullyIntegratedPreverifiedTool}
            >
              <IsReadOnlyModeContext.Provider value={isMainContentReadOnly}>
                <Step2
                  epdId={id}
                  epdVersionId={epdVersionId}
                  tab={tab}
                  epdDefinition={epdDefinition}
                  validationState={compilerCompositeStatus}
                />
              </IsReadOnlyModeContext.Provider>
            </IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider>
          </PcrV2Context.Provider>
        );
      },
      stepState: (errors) => compilerStepsValidator.getStepState(2, errors),
    },
    {
      id: 'step3',
      label: 'Step 3',
      name: 'LCA information',
      description: 'Describe your LCA output',
      path: EpdLinks.epdCompilerSpecific(id, { step: 'step3', tab: 'specification' }),
      render: () => {
        return (
          <PcrV2Context.Provider value={isPcrV2}>
            <IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider
              value={isCreatedByApiFullyIntegratedPreverifiedTool}
            >
              <IsReadOnlyModeContext.Provider value={isMainContentReadOnly}>
                <Step3 epdId={id} epdVersionId={epdVersionId!} tab={tab} validationState={compilerCompositeStatus} />
              </IsReadOnlyModeContext.Provider>
            </IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider>
          </PcrV2Context.Provider>
        );
      },
      stepState: (errors) => compilerStepsValidator.getStepState(3, errors),
    },
    {
      id: 'step4',
      label: 'Step 4',
      name: 'EPD verification',
      description: 'Get the third-party approval',
      path: EpdLinks.epdCompilerSpecific(id, { step: 'step4' }),
      render: () => {
        return (
          <PcrV2Context.Provider value={isPcrV2}>
            <IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider
              value={isCreatedByApiFullyIntegratedPreverifiedTool}
            >
              <Step4 epdId={id} epdVersionId={epdVersionId!} validationState={compilerCompositeStatus} />
            </IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider>
          </PcrV2Context.Provider>
        );
      },
      stepState: (errors) => compilerStepsValidator.getStepState(4, errors),
    },
    {
      id: 'step5',
      label: 'Step 5',
      name: 'EPD publication',
      description: 'Submit the EPD for publication',
      path: EpdLinks.epdCompilerSpecific(id, { step: 'step5' }),
      render: () => {
        return (
          <PcrV2Context.Provider value={isPcrV2}>
            <IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider
              value={isCreatedByApiFullyIntegratedPreverifiedTool}
            >
              <IsReadOnlyModeContext.Provider value={isMainContentReadOnly}>
                <Step5 epdId={id} epdVersionId={epdVersionId!} validationState={compilerCompositeStatus} />
              </IsReadOnlyModeContext.Provider>
            </IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider>
          </PcrV2Context.Provider>
        );
      },
      stepState: (errors) => compilerStepsValidator.getStepState(5, errors),
    },
    {
      id: 'step6',
      label: 'Step 6',
      name: 'Generated EPD',
      description: 'Get your EPD in PDF format',
      path: EpdLinks.epdCompilerSpecific(id, { step: 'step6' }),
      render: () => {
        return (
          <PcrV2Context.Provider value={isPcrV2}>
            <IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider
              value={isCreatedByApiFullyIntegratedPreverifiedTool}
            >
              <Step6 epdId={id} epdVersionId={epdVersionId} />
            </IsCreatedByApiFullyIntegratedPreverifiedToolContext.Provider>
          </PcrV2Context.Provider>
        );
      },
      stepState: () => undefined,
    },
  ];

  return {
    epdId: id,
    epdVersionId,
    history,
    stepId,
    redirect,
    steps,
    compilerCompositeStatus,
  };
};

export default useCompilerLogic;
